import { AnswerType } from './types/answerType';
import {
  ChecklistItemWithAnswers,
  FormChecklistAnswer,
} from './types/formAnswer';
import { ChecklistAnswerOutDto } from 'shared/dtos/out/checklistAnswer';
import { Translate } from 'intl/translate';
import {
  withDefaultComplianceOptionLabels,
} from 'shared/domain/compliance/complianceOptionLabels';

/**
 * Represents changes to answers for a single checklist item.
 */
export interface AnswerChangeSet {
  itemId: string;
  created: FormChecklistAnswer[];
  updated: FormChecklistAnswer[];
  deleted: string[];
}

/**
 * Represents the overall answer changes across all checklist items.
 */
export interface AnswerChanges {
  changeSets: AnswerChangeSet[];
  hasChanges: boolean;
}

interface AnswerIdentifiable {
  _id?: string;
  uniqueKeyId: string;
}

/**
 * Extracts the identifier from an answer (prefers _id over uniqueKeyId).
 */
function getAnswerId(answer: AnswerIdentifiable): string {
  return answer._id || answer.uniqueKeyId;
}

/**
 * Compares two compliance option labels arrays for equality.
 */
function areOptionLabelsEqual(
  a: Array<{ option: string; label: string }>,
  b: Array<{ option: string; label: string }>
): boolean {
  if (a.length !== b.length) return false;
  return a.every((optA, index) => {
    const optB = b[index];
    return optA.option === optB.option && optA.label === optB.label;
  });
}

/**
 * Compares two selection options arrays for equality.
 */
function areSelectionOptionsEqual(
  a: Array<{ _id: string; label: string }>,
  b: Array<{ _id: string; label: string }>
): boolean {
  if (a.length !== b.length) return false;
  return a.every((optA, index) => {
    const optB = b[index];
    return optA._id === optB._id && optA.label === optB.label;
  });
}

/**
 * Determines if an answer has been modified by comparing old and new values.
 * Uses type-specific comparison for different answer types.
 */
function hasAnswerChanged(
  oldAnswer: FormChecklistAnswer,
  newAnswer: FormChecklistAnswer
): boolean {
  if (oldAnswer.type !== newAnswer.type) return true;
  if (oldAnswer.label !== newAnswer.label) return true;
  if (oldAnswer.isRequired !== newAnswer.isRequired) return true;

  switch (newAnswer.type) {
    case AnswerType.Compliance:
      if (oldAnswer.type !== AnswerType.Compliance) {
        return true;
      }
      return !areOptionLabelsEqual(
        oldAnswer.optionLabels,
        newAnswer.optionLabels
      );

    case AnswerType.Numeric:
      if (oldAnswer.type !== AnswerType.Numeric) {
        return true;
      }
      return (
        oldAnswer.unit !== newAnswer.unit ||
        oldAnswer.normMin !== newAnswer.normMin ||
        oldAnswer.normMax !== newAnswer.normMax ||
        oldAnswer.rangeMin !== newAnswer.rangeMin ||
        oldAnswer.rangeMax !== newAnswer.rangeMax ||
        oldAnswer.step !== newAnswer.step
      );

    case AnswerType.Text:
      return false;

    case AnswerType.OptionSelection:
      if (oldAnswer.type !== AnswerType.OptionSelection) {
        return true;
      }
      return (
        oldAnswer.isMultiselect !== newAnswer.isMultiselect ||
        !areSelectionOptionsEqual(oldAnswer.options, newAnswer.options)
      );

    default:
      return false;
  }
}

/**
 * Creates a change set for a single checklist item by comparing old and new answers.
 */
function createItemAnswerChangeSet(
  itemId: string,
  oldAnswers: FormChecklistAnswer[],
  newAnswers: FormChecklistAnswer[]
): AnswerChangeSet {
  const oldAnswersMap = new Map<string, FormChecklistAnswer>();
  oldAnswers.forEach((answer) => {
    const id = getAnswerId(answer);
    oldAnswersMap.set(id, answer);
  });

  const newAnswersMap = new Map<string, FormChecklistAnswer>();
  newAnswers.forEach((answer) => {
    const id = getAnswerId(answer);
    newAnswersMap.set(id, answer);
  });

  const created: FormChecklistAnswer[] = [];
  const updated: FormChecklistAnswer[] = [];
  const deleted: string[] = [];

  newAnswers.forEach((newAnswer) => {
    const answerId = getAnswerId(newAnswer);
    const oldAnswer = oldAnswersMap.get(answerId);

    if (!oldAnswer) {
      if (!newAnswer._id) {
        created.push(newAnswer);
      }
    } else if (hasAnswerChanged(oldAnswer, newAnswer)) {
      updated.push(newAnswer);
    }
  });

  oldAnswers.forEach((oldAnswer) => {
    if (oldAnswer._id) {
      const answerId = getAnswerId(oldAnswer);
      if (!newAnswersMap.has(answerId)) {
        deleted.push(oldAnswer._id);
      }
    }
  });

  return { itemId, created, updated, deleted };
}

export function computeAnswerChanges(
  oldItems: readonly ChecklistItemWithAnswers[],
  newItems: readonly ChecklistItemWithAnswers[]
): AnswerChanges {
  const changeSets: AnswerChangeSet[] = [];

  const oldItemsMap = new Map<string, ChecklistItemWithAnswers>();
  oldItems.forEach((item) => {
    if (item._id) {
      oldItemsMap.set(item._id, item);
    }
  });

  newItems.forEach((newItem) => {
    // New items (no server _id yet) are synced via createAnswersForNewItems.
    if (!newItem._id) {
      return;
    }

    const newItemId = newItem._id;

    const oldItem = oldItemsMap.get(newItemId);
    const oldAnswers = oldItem?.answers || [];
    const newAnswers = newItem.answers || [];

    if (oldAnswers.length === 0 && newAnswers.length === 0) {
      return;
    }

    const changeSet = createItemAnswerChangeSet(newItemId, oldAnswers, newAnswers);

    if (
      changeSet.created.length > 0 ||
      changeSet.updated.length > 0 ||
      changeSet.deleted.length > 0
    ) {
      changeSets.push(changeSet);
    }
  });

  return {
    changeSets,
    hasChanges: changeSets.length > 0,
  };
}

/**
 * Converts a FormChecklistAnswer to ChecklistAnswerOutDto for API calls.
 * Empty compliance option labels are filled with i18n defaults when
 * `formatMessage` is provided (user locale at save time).
 */
export function formAnswerToOutDto(
  answer: FormChecklistAnswer,
  translate?: Translate
): ChecklistAnswerOutDto {
  const base = {
    type: answer.type,
    label: answer.label,
    isRequired: answer.isRequired,
  };

  switch (answer.type) {
    case AnswerType.Compliance:
      return {
        ...base,
        type: AnswerType.Compliance,
        optionLabels: translate
          ? withDefaultComplianceOptionLabels(
              answer.optionLabels,
              translate
            )
          : answer.optionLabels,
      };

    case AnswerType.Numeric:
      return {
        ...base,
        type: AnswerType.Numeric,
        unit: answer.unit,
        normMin: answer.normMin,
        normMax: answer.normMax,
        rangeMin: answer.rangeMin,
        rangeMax: answer.rangeMax,
        step: answer.step,
      };

    case AnswerType.Text:
      return {
        ...base,
        type: AnswerType.Text,
      };

    case AnswerType.OptionSelection:
      return {
        ...base,
        type: AnswerType.OptionSelection,
        options: answer.options,
        isMultiselect: answer.isMultiselect,
      };
  }
}
